import { AfterViewInit, Component, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TrialPolicy, UpdateEdiaryAutoPaymentRequest } from '../../trial-policy.model';
import { TrialService } from '../../../../../core/services/trial.service';
import { AlertService } from '../../../../../shared/alert/alert.service';
import { Currencies } from '../../../../../core/constants/currency';
import { DropdownInputComponent } from '../../../../../shared/dropdown-input/dropdown-input.component';
import { LogHelper } from '../../../../../core/helpers/log.helper';

@Component({
  selector: 'app-trial-policy-ediary-payments',
  templateUrl: './trial-policy-ediary-payments.component.html',
  styleUrl: './trial-policy-ediary-payments.component.scss'
})
export class TrialPolicyEdiaryPaymentsComponent implements OnInit, AfterViewInit {
  @ViewChild('currencySelect') currencySelect: DropdownInputComponent;

  @Input('policy') policy: TrialPolicy;
  @Input() policyId: string;
  @Input() trialId: string;

  @Output() changed = new EventEmitter();

  private readonly _trialService = inject(TrialService);
  private readonly _alertService = inject(AlertService);

  ediaryForm: UntypedFormGroup;
  currencies: { value: string, text: string }[] = [];

  ngOnInit() {
    this.ediaryForm = new UntypedFormGroup({
      isBusy: new FormControl<boolean>(false),
      ediaryAutoPaymentEnabled: new UntypedFormControl(false),
      ediaryAutoPaymentCurrency: new UntypedFormControl(null),
      ediaryAutoPaymentAmount: new UntypedFormControl(null),
    });

    this.initiateCurrencies();
    this.subscribeFormChanges();
  }

  ngAfterViewInit() {
    this.populateForm();
  }

  initiateCurrencies() {
    this.currencies = Currencies.all().map((x) => ({ value: x.cc, text: `${x.name} (${x.cc})` }));
  }

  subscribeFormChanges() {
    this.ediaryForm.get('ediaryAutoPaymentEnabled').valueChanges.subscribe(enabled => {
      this.updateValidation(enabled);
    });
  }

  private updateValidation(enabled: boolean) {
    const currencyControl = this.ediaryForm.get('ediaryAutoPaymentCurrency');
    const amountControl = this.ediaryForm.get('ediaryAutoPaymentAmount');

    if (enabled) {
      currencyControl.setValidators(Validators.required);
      amountControl.setValidators([Validators.required, Validators.min(0.01)]);
    } else {
      currencyControl.clearValidators();
      amountControl.clearValidators();
    }

    currencyControl.updateValueAndValidity();
    amountControl.updateValueAndValidity();
  }

  populateForm() {
    if (!this.policy) return;

    this.ediaryForm.patchValue({
      ediaryAutoPaymentEnabled: this.policy.ediaryAutoPaymentEnabled,
      ediaryAutoPaymentCurrency: this.policy.ediaryAutoPaymentCurrency,
      ediaryAutoPaymentAmount: this.policy.ediaryAutoPaymentAmount,
    });

    if (this.policy.ediaryAutoPaymentCurrency) {
      setTimeout(() => {
        this.currencySelect?.setValue(this.policy.ediaryAutoPaymentCurrency);
      }, 10);
    }

    this.updateValidation(this.policy.ediaryAutoPaymentEnabled);
  }

  onSave() {
    this.ediaryForm.patchValue({ isBusy: true });

    const request: UpdateEdiaryAutoPaymentRequest = {
      ediaryAutoPaymentEnabled: this.ediaryForm.get('ediaryAutoPaymentEnabled').value,
      ediaryAutoPaymentCurrency: this.ediaryForm.get('ediaryAutoPaymentCurrency').value,
      ediaryAutoPaymentAmount: this.ediaryForm.get('ediaryAutoPaymentAmount').value,
    };

    this._trialService.updateEdiaryAutoPayment(this.policyId, request).subscribe({
      next: () => {
        this.ediaryForm.patchValue({ isBusy: false });
        this._alertService.showSuccessAlert('Ediary auto-payment settings updated successfully.');
        this.changed.emit();
      },
      error: error => {
        this.ediaryForm.patchValue({ isBusy: false });
        this._alertService.showWarningAlert(error.error?.title || 'An error occurred.');
        LogHelper.log(error);
      }
    });
  }
}
